import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Back Button */}
        <div className="mb-8">
          <Button
            onClick={() => window.history.back()}
            variant="ghost"
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-black text-slate-900 uppercase tracking-tighter mb-4">
            Privacy Policy
          </h1>
          <p className="text-slate-600 text-sm">
            Last Updated: {new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-[2rem] border-2 border-slate-100 p-8 md:p-12 shadow-sm space-y-8">
          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">1. Who We Are</h2>
            <p className="text-slate-700 leading-relaxed">
              VaultStack Small Business Platform ("we", "us", "our") is operated by VaultStack Pty Ltd 
              (ABN 95 691 143 665). We provide business management software for Australian small businesses.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">2. Information We Collect</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We collect information that you provide directly to us, including:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
              <li>Name, email address, and phone number</li>
              <li>Business details (business name, ABN, address)</li>
              <li>Payment information (processed securely via Stripe)</li>
              <li>Customer data you input into the platform</li>
              <li>Usage data and analytics</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
              <li>Provide, maintain, and improve our services</li>
              <li>Process your transactions and send notifications</li>
              <li>Send you technical notices and support messages</li>
              <li>Respond to your comments and questions</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">4. Data Storage & Security</h2>
            <p className="text-slate-700 leading-relaxed">
              Your data is stored securely using Supabase, Inc., a leading cloud database provider. 
              We implement industry-standard security measures to protect your personal information. 
              All payment transactions are processed through Stripe, Inc., a PCI-DSS compliant payment processor. 
              We do not store your credit card information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">5. Information Sharing</h2>
            <p className="text-slate-700 leading-relaxed">
              We do not sell your personal information. We share your information only with your consent, 
              to comply with laws, or to protect our rights. We may share data with service providers who 
              perform services on our behalf (e.g., payment processing, email delivery).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">6. Your Rights</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              You have the right to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4">
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Export your data in a portable format</li>
              <li>Opt-out of marketing communications</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">7. Cookies</h2>
            <p className="text-slate-700 leading-relaxed">
              We use cookies and similar tracking technologies to operate and improve our service. 
              You can control cookies through your browser settings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">8. Children's Privacy</h2>
            <p className="text-slate-700 leading-relaxed">
              Our service is not intended for children under 18. We do not knowingly collect personal 
              information from children.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">9. Changes to This Policy</h2>
            <p className="text-slate-700 leading-relaxed">
              We may update this privacy policy from time to time. We will notify you of any changes by 
              posting the new policy on this page and updating the "Last Updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-black text-slate-900 uppercase mb-4">10. Contact Us</h2>
            <p className="text-slate-700 leading-relaxed">
              For any questions about this Privacy Policy, please contact us at:{' '}
              <a href="mailto:privacy@vaultstack.com.au" className="text-purple-600 hover:underline font-bold">
                privacy@vaultstack.com.au
              </a>
            </p>
          </section>

          <section className="border-t-2 border-slate-100 pt-8">
            <h2 className="text-xl font-black text-slate-900 uppercase mb-4">Australian Privacy Principles</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              We comply with the Australian Privacy Principles under the Privacy Act 1988 (Cth). 
              You may lodge a complaint with the Office of the Australian Information Commissioner (OAIC) 
              if you believe we have mishandled your personal information.
            </p>
          </section>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-8 text-xs text-slate-500 font-bold uppercase tracking-widest">
          VaultStack Pty Ltd ABN 95 691 143 665
        </div>
      </div>
    </div>
  );
}
